import { CompanyReference, ICompany } from './CompanyStore';
import { ContactReference, IContact } from './ContactStore';
import {
  flow,
  getRoot,
  IAnyModelType,
  Instance,
  SnapshotIn,
  SnapshotOut,
  types,
} from 'mobx-state-tree';
import { modelWithCRUD } from '../utils/modelWithCRUD';
import { RootModelType } from '../Root';

export const ContactGroup = types.model('ContactGroup', {
  id: types.union(types.identifier, types.identifierNumber),
  name: types.string,
  description: types.maybeNull(types.string),
  contacts: types.array(types.late(() => ContactReference)),
  companies: types.array(types.late(() => CompanyReference)),
});

export const ContactGroupStore = modelWithCRUD('contact-groups', ContactGroup)
  .named('ContactGroupStore')
  .actions((self) => ({
    manageContactsAndCompanies: flow(function* addContactsAndCompanies(
      group: IContactGroup,
      {
        contacts,
        companies,
        deleteItems = false,
      }: {
        contacts?: IContact[];
        companies?: ICompany[];
        deleteItems?: boolean;
      }
    ) {
      const res = yield self.env.api.put(
        `${self.apiPath}/${group.id}/contacts/`,
        {
          delete: deleteItems,
          contacts: contacts ? contacts.map((c) => c.id) : undefined,
          companies: companies ? companies.map((c) => c.id) : undefined,
        }
      );
      self.putItem(res.data);
      const root = getRoot(self) as RootModelType;
      self.invalidateCaches();
      if (contacts && contacts.length > 0) {
        root.contactStore.invalidateCaches();
      }
      if (companies && companies.length > 0) {
        root.companyStore.invalidateCaches();
      }
      return group;
    }),
  }));

export const ContactGroupReference = types.reference(
  types.late((): IAnyModelType => ContactGroup),
  {
    get(identifier, parent): any {
      return (getRoot(parent!) as RootModelType).contactGroupStore.getItemNaive(
        identifier
      );
    },
    set(value) {
      return value.id;
    },
  }
);

export interface IContactGroup extends Instance<typeof ContactGroup> {}
export interface IContactGroupSnapshotIn
  extends SnapshotIn<typeof ContactGroup> {}
export interface IContactGroupSnapshotOut
  extends SnapshotOut<typeof ContactGroup> {}
